import type { StateCreator } from 'zustand';
import type {
  DefinitionFormStoreState,
  SecurityScoringSlice,
} from '../types/definitionFormStoreTypes';

export const createSecurityScoringSlice: StateCreator<
  DefinitionFormStoreState,
  [['zustand/immer', never]],
  [],
  SecurityScoringSlice
> = (set) => ({
  steps: { long: [], short: [] },
  buffer: {
    isEnabled: false,
    methodology: 'rank',
    rankIn: 5,
    rankOut: 10,
    percentileIn: 80,
    percentileOut: 70,
  },
  countryDecomposition: {
    isEnabled: false,
    method: 'index_weight',
    minWeightPct: null,
    customWeights: [],
  },

  setDecomposeByCountry: (enabled) =>
    set((state) => {
      state.countryDecomposition.isEnabled = enabled;
    }),

  setCountryAllocationMethod: (method) =>
    set((state) => {
      state.countryDecomposition.method = method;
    }),

  setCountryMinWeightPct: (value) =>
    set((state) => {
      state.countryDecomposition.minWeightPct = value;
    }),

  setCountryCustomWeights: (weights) =>
    set((state) => {
      state.countryDecomposition.customWeights = weights;
    }),

  toggleBuffer: () =>
    set((state) => {
      state.buffer.isEnabled = !state.buffer.isEnabled;
    }),

  setBufferMethodology: (method) =>
    set((state) => {
      state.buffer.methodology = method;
    }),

  setBufferRankIn: (value) =>
    set((state) => {
      state.buffer.rankIn = value;
    }),

  setBufferRankOut: (value) =>
    set((state) => {
      state.buffer.rankOut = value;
    }),

  setBufferPercentileIn: (value) =>
    set((state) => {
      state.buffer.percentileIn = value;
    }),

  setBufferPercentileOut: (value) =>
    set((state) => {
      state.buffer.percentileOut = value;
    }),

  addStep: (leg, step) =>
    set((state) => {
      state.steps[leg].push({
        ...step,
        id: crypto.randomUUID(),
      });
    }),

  removeStep: (leg, stepId) =>
    set((state) => {
      const newSteps = state.steps[leg].filter((s) => s.id !== stepId);
      state.steps[leg] = newSteps;
      const bothEmpty =
        state.steps.long.length === 0 && state.steps.short.length === 0;
      if (bothEmpty) {
        state.buffer.isEnabled = false;
      }
    }),

  updateStep: (leg, stepId, step) =>
    set((state) => {
      const index = state.steps[leg].findIndex((s) => s.id === stepId);
      if (index !== -1) {
        state.steps[leg][index] = { ...step, id: stepId };
      }
    }),

  moveStep: (leg, stepId, direction) =>
    set((state) => {
      const steps = state.steps[leg];
      const index = steps.findIndex((s) => s.id === stepId);
      if (index === -1) return;

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= steps.length) return;

      const temp = steps[index];
      steps[index] = steps[targetIndex];
      steps[targetIndex] = temp;
    }),

  addMetricToStep: (leg, stepId, metricName) =>
    set((state) => {
      const step = state.steps[leg].find((s) => s.id === stepId);
      if (step) {
        step.metrics.push({
          id: crypto.randomUUID(),
          name: metricName,
          description: '',
          category: '',
          lookback: '1Y',
          normalization: 'None',
          inverse: false,
          weight: step.metrics.length > 0 ? 0 : 100,
          configurations: {},
        });
      }
    }),

  updateMetric: (leg, stepId, metricId, updates) =>
    set((state) => {
      const step = state.steps[leg].find((s) => s.id === stepId);
      const metric = step?.metrics.find((m) => m.id === metricId);
      if (metric) {
        Object.assign(metric, updates);
      }
    }),

  removeMetric: (leg, stepId, metricId) =>
    set((state) => {
      const step = state.steps[leg].find((s) => s.id === stepId);
      if (step) {
        step.metrics = step.metrics.filter((m) => m.id !== metricId);
      }
    }),

  updateStepConfig: (leg, stepId, updates) =>
    set((state) => {
      const step = state.steps[leg].find((s) => s.id === stepId);
      if (!step) return;

      if (updates.selection && step.selection) {
        Object.assign(step.selection, updates.selection);
      }
      if (updates.threshold && step.threshold) {
        Object.assign(step.threshold, updates.threshold);
      }

      const { selection, threshold, ...rest } = updates;
      if (Object.keys(rest).length > 0) {
        Object.assign(step, rest);
      }
    }),
});
